import { Contract, PaymentData } from '../types/contract';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { formatDate, fromISOString } from '../utils/dateUtils';
import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc, 
  query, 
  orderBy, 
  where,
  Timestamp 
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL 
} from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { log } from '../utils/logger';

// Serviço de contratos
export const contractService = {
  // Salvar contrato
  async saveContract(contract: Contract): Promise<string> {
    try {
      log.info('Iniciando salvamento de contrato no Firebase', 'contractService.ts', {
        contratante: contract.contratante,
        telefone: contract.telefone,
        dataFesta: contract.dataFesta
      });

      const contractData = {
        ...contract,
        timestamp: Timestamp.now(),
        createdAt: Timestamp.now()
      };
      
      const docRef = await addDoc(collection(db, 'contracts'), contractData);
      
      log.info('Contrato salvo no Firebase com sucesso', 'contractService.ts', { 
        contractId: docRef.id,
        contratante: contract.contratante
      });
      
      return docRef.id;
    } catch (error) {
      log.error('Erro ao salvar contrato no Firebase', 'contractService.ts', error);
      console.error('Erro ao salvar contrato:', error);
      throw error;
    }
  },

  // Buscar todos os contratos
  async getContracts(): Promise<Contract[]> {
    try {
      const q = query(collection(db, 'contracts'), orderBy('timestamp', 'desc'));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      })) as Contract[];
    } catch (error) {
      console.error('Erro ao buscar contratos:', error);
      throw error;
    }
  },

  // Buscar contratos por status
  async getContractsByStatus(status: string): Promise<Contract[]> {
    try {
      const q = query(
        collection(db, 'contracts'), 
        where('status', '==', status),
        orderBy('timestamp', 'desc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      })) as Contract[];
    } catch (error) {
      console.error('Erro ao buscar contratos por status:', error);
      throw error;
    }
  },

  // Buscar contrato por ID
  async getContractById(id: string): Promise<Contract | null> {
    try {
      const querySnapshot = await getDocs(collection(db, 'contracts'));
      const contract = querySnapshot.docs.find(doc => doc.id === id);
      return contract ? { ...contract.data(), id: contract.id } as Contract : null;
    } catch (error) {
      console.error('Erro ao buscar contrato:', error);
      throw error;
    }
  },

  // Atualizar contrato
  async updateContract(id: string, updates: Partial<Contract>): Promise<void> {
    try {
      // O Firestore rejeita updateDoc() se qualquer campo vier undefined
      // (ex: campos opcionais do contrato que nunca foram preenchidos).
      // Removemos esses campos em vez de tentar gravá-los.
      const cleanUpdates: Record<string, unknown> = {};
      Object.entries(updates).forEach(([key, value]) => {
        if (value !== undefined) cleanUpdates[key] = value;
      });

      log.info('Atualizando contrato no Firebase', 'contractService.ts', { contractId: id, updates: cleanUpdates });

      const docRef = doc(db, 'contracts', id);
      await updateDoc(docRef, {
        ...cleanUpdates,
        updatedAt: Timestamp.now()
      });

      log.info('Contrato atualizado com sucesso', 'contractService.ts', { contractId: id });
    } catch (error) {
      log.error('Erro ao atualizar contrato no Firebase', 'contractService.ts', error);
      console.error('Erro ao atualizar contrato:', error);
      throw error;
    }
  },

  // Excluir contrato
  async deleteContract(id: string): Promise<void> {
    try {
      const docRef = doc(db, 'contracts', id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Erro ao excluir contrato:', error);
      throw error;
    }
  },

  // Gerar PDF do contrato
  async generateContractPDF(contract: Contract): Promise<Blob> {
    return new Promise<Blob>(async (resolve, reject) => {
      try {
        const pdf = new jsPDF('p', 'mm', 'a4');
        
        // Configurações de fonte e cores
        pdf.setFont('helvetica');
        pdf.setFontSize(16);
        pdf.setTextColor(255, 108, 0); // Cor laranja PlayKids
        
        // Cabeçalho
        pdf.text('CONTRATO PLAYKIDS', 105, 20, { align: 'center' });
        
        // Linha separadora
        pdf.setDrawColor(255, 108, 0);
        pdf.setLineWidth(0.5);
        pdf.line(20, 25, 190, 25);
        
        // Resetar cor para texto
        pdf.setTextColor(0, 0, 0);
        pdf.setFontSize(12);
        
        let yPosition = 40;
        
        // Dados do Contratante
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('DADOS DO CONTRATANTE', 20, yPosition);
        yPosition += 8;
        
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Nome: ${contract.contratante}`, 20, yPosition);
        yPosition += 6;
        pdf.text(`CPF: ${contract.cpf}`, 20, yPosition);
        yPosition += 6;
        pdf.text(`Telefone: ${contract.telefone}`, 20, yPosition);
        yPosition += 6;
        pdf.text(`Endereço: ${contract.endereco}, ${contract.numero} - ${contract.bairro}`, 20, yPosition);
        yPosition += 6;
        pdf.text(`${contract.cidade} - ${contract.estado}`, 20, yPosition);
        yPosition += 10;
        
        // Dados da Festa
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('DADOS DA FESTA', 20, yPosition);
        yPosition += 8;
        
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Aniversariante: ${contract.nomeAniversariante}`, 20, yPosition);
        yPosition += 6;
        pdf.text(`Data: ${contract.dataFesta ? formatDate(fromISOString(contract.dataFesta)) : 'A definir'}`, 20, yPosition);
        yPosition += 6;
        pdf.text(`Horário: ${contract.horarioInicioFesta || 'A definir'}`, 20, yPosition);
        yPosition += 6;
        pdf.text(`Local: ${contract.enderecoFesta || contract.endereco}`, 20, yPosition);
        yPosition += 6;
        pdf.text(`Tema: ${contract.temaFesta || 'A definir'}`, 20, yPosition);
        yPosition += 6;
        pdf.text(`Número de Convidados: ${contract.numeroConvidados || 'A definir'}`, 20, yPosition);
        yPosition += 10;
        
        // Serviços
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('SERVIÇOS', 20, yPosition);
        yPosition += 8;
        
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Pacote de Atividades: ${contract.pacoteAtividades || 'A definir'}`, 20, yPosition);
        yPosition += 6;
        pdf.text(`Início da Recreação: ${contract.inicioRecreacao || 'A definir'}`, 20, yPosition);
        yPosition += 6;
        pdf.text(`Fim da Recreação: ${contract.fimRecreacao || 'A definir'}`, 20, yPosition);
        yPosition += 10;
        
        // Verificar se precisa de nova página
        if (yPosition > 250) {
          pdf.addPage();
          yPosition = 20;
        }
        
        // Informações de Pagamento
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('INFORMAÇÕES DE PAGAMENTO', 20, yPosition);
        yPosition += 8;
        
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Valor: ${contract.valor ? `R$ ${contract.valor}` : 'A definir'}`, 20, yPosition);
        yPosition += 6;
        pdf.text(`Forma de Pagamento: ${contract.formaPagamento || 'A definir'}`, 20, yPosition);
        yPosition += 6;
        pdf.text(`Status: ${contract.status || 'Pendente'}`, 20, yPosition);
        yPosition += 10;
        
        // Observações (se existirem)
        if (contract.observacoes && contract.observacoes.trim()) {
          pdf.setFontSize(14);
          pdf.setFont('helvetica', 'bold');
          pdf.text('OBSERVAÇÕES', 20, yPosition);
          yPosition += 8;
          
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'normal');
          
          // Quebrar texto em múltiplas linhas se necessário
          const maxWidth = 170;
          const words = contract.observacoes.split(' ');
          let line = '';
          
          for (let word of words) {
            const testLine = line + word + ' ';
            const testWidth = pdf.getTextWidth(testLine);
            
            if (testWidth > maxWidth && line !== '') {
              pdf.text(line, 20, yPosition);
              yPosition += 6;
              line = word + ' ';
            } else {
              line = testLine;
            }
          }
          
          if (line) {
            pdf.text(line, 20, yPosition);
            yPosition += 6;
          }
        }
        
        // Verificar se precisa de nova página para rodapé
        if (yPosition > 270) {
          pdf.addPage();
          yPosition = 20;
        }
        
        // Rodapé
        yPosition = Math.max(yPosition + 10, 270);
        pdf.setDrawColor(200, 200, 200);
        pdf.setLineWidth(0.2);
        pdf.line(20, yPosition, 190, yPosition);
        yPosition += 5;
        
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        pdf.text('Este documento foi gerado automaticamente pelo sistema PlayKids', 105, yPosition, { align: 'center' });
        yPosition += 4;
        pdf.text(`Data de geração: ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}`, 105, yPosition, { align: 'center' });
        
        const pdfBlob = pdf.output('blob');
        resolve(pdfBlob);
      } catch (error) {
        console.error('Erro ao gerar PDF:', error);
        reject(error);
      }
    });
  },

  // Importar contrato individual
  async importContract(contractData: any): Promise<string> {
    try {
      // Verificar se já existe contrato com mesmo CPF e data
      const existingContracts = await this.getContracts();
      const duplicate = existingContracts.find(c => 
        c.cpf === contractData.cpf && 
        c.dataFesta === contractData.dataFesta
      );
      
      if (duplicate) {
        throw new Error('Contrato com mesmo CPF e data já existe');
      }
      
      return await this.saveContract(contractData);
    } catch (error) {
      console.error('Erro ao importar contrato:', error);
      throw error;
    }
  },

  // Importar múltiplos contratos
  async importContracts(contractsData: any[]): Promise<{ success: number; errors: string[]; totalProcessed: number }> {
    const results = { success: 0, errors: [] as string[], totalProcessed: contractsData.length };
    
    for (const contractData of contractsData) {
      try {
        await this.saveContract(contractData);
        results.success++;
      } catch (error) {
        results.errors.push(`Erro ao importar contrato: ${error}`);
      }
    }
    
    return results;
  },

  // Buscar contratos com filtros
  async searchContracts(filters: {
    status?: string;
    dataInicio?: Date;
    dataFim?: Date;
    contratante?: string;
    cpf?: string;
  }): Promise<Contract[]> {
    try {
      let q = query(collection(db, 'contracts'), orderBy('timestamp', 'desc'));
      
      if (filters.status) {
        q = query(q, where('status', '==', filters.status));
      }
      
      if (filters.contratante) {
        q = query(q, where('contratante', '>=', filters.contratante));
      }
      
      if (filters.cpf) {
        q = query(q, where('cpf', '==', filters.cpf));
      }
      
      const querySnapshot = await getDocs(q);
      let contracts = querySnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      })) as Contract[];
      
      // Filtros de data aplicados no cliente
      if (filters.dataInicio || filters.dataFim) {
        contracts = contracts.filter(contract => {
          if (!contract.dataFesta) return false;
          
          const contractDate = new Date(contract.dataFesta);
          
          if (filters.dataInicio && contractDate < filters.dataInicio) return false;
          if (filters.dataFim && contractDate > filters.dataFim) return false;
          
          return true;
        });
      }
      
      return contracts;
    } catch (error) {
      console.error('Erro ao buscar contratos com filtros:', error);
      throw error;
    }
  },

  // Buscar contratos onde o funcionário participou
  async getContractsByEmployee(employeeId: string): Promise<Array<{
    contractId: string;
    nomeAniversariante: string;
    dataFesta: string;
    valorPagamento: number;
    funcao: string;
  }>> {
    try {
      const contracts = await this.getContracts();
      
      const employeeContracts = contracts
        .filter(contract => 
          contract.funcionarios?.some(func => func.funcionarioId === employeeId)
        )
        .map(contract => {
          const funcionarioInfo = contract.funcionarios?.find(func => func.funcionarioId === employeeId);
          
          // Validar e formatar a data
          let dataFesta = contract.dataFesta || '';
          
          return {
            contractId: contract.id || '',
            nomeAniversariante: contract.nomeAniversariante || 'Evento sem nome',
            dataFesta: dataFesta,
            valorPagamento: funcionarioInfo?.valorPagamento || 0,
            funcao: funcionarioInfo?.funcao || 'Função não especificada'
          };
        })
        .sort((a, b) => new Date(b.dataFesta).getTime() - new Date(a.dataFesta).getTime()); // Mais recentes primeiro
      
      return employeeContracts;
    } catch (error) {
      console.error('Erro ao buscar contratos do funcionário:', error);
      throw error;
    }
  }
};
